import { produce } from 'immer'
import { commandCenterSort } from './commandCenter.helpers'

// Action Types
export const CONNECT = 'commandCenter/connect'
export const DISCONNECT = 'commandCenter/disconnect'
export const CHANGE_SUBSCRIPTION = 'commandCenter/changeSubscription'
export const RECEIVE_BROADCAST = 'commandCenter/receiveBroadcast'
export const SET_AGENTS = 'commandCenter/setAgents'
export const SET_AGENT = 'commandCenter/setAgent'
export const SET_SENTIMENT_DATA = 'commandCenter/setSentimentData'
export const SET_SENTIMENT_CONFIG = 'commandCenter/setSentimentConfig'
export const SET_LOADING = 'commandCenter/setLoading'
export const START_LISTENING = 'commandCenter/startListening'
export const ATTEMPT_LISTENING = 'commandCenter/attemptListening'
export const STOP_LISTENING = 'commandCenter/stopListening'
export const SET_AGENTS_AUDIO_ISSUES = 'commandCenter/setAgentsAudioIssues'
// The live half of a row, straight off a lifecycle event. The server reads it
// once per boundary so the client doesn't have to ask per manager.
export const PATCH_AGENT_CALL_DATA = 'commandCenter/patchAgentCallData'
// Whether the roster is on screen. Only then does every agent's row need to be
// kept current; off the page the socket still runs for the widget, and
// refetching a row per lifecycle event for the whole team is pure load.
export const SET_ROSTER_IN_USE = 'commandCenter/setRosterInUse'
export const SET_FILTERS_LOADED = 'commandCenter/setFiltersLoaded'
export const SET_SAVED_FILTER_ID = 'commandCenter/setSavedFilterId'
export const SOCKET_OPEN = 'commandCenter/socketOpen'
export const SOCKET_CLOSED = 'commandCenter/socketClosed'
export const SEND_CHAT = 'commandCenter/sendChat'

// Action Creators
export const connectCCSocket = () => ({ type: CONNECT })
export const disconnectCCSocket = () => ({ type: DISCONNECT })
export const changeSubscriptionCCSocket = (payload) => ({ type: CHANGE_SUBSCRIPTION, payload })
export const setAgents = (payload) => ({ type: SET_AGENTS, payload })
export const setAgent = (payload) => ({ type: SET_AGENT, payload })
export const setSentimentData = (payload) => ({ type: SET_SENTIMENT_DATA, payload })
export const setSentimentConfig = (payload) => ({ type: SET_SENTIMENT_CONFIG, payload })
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const stopListeningToCall = () => ({ type: STOP_LISTENING })
export const attemptListeningToCall = (payload) => ({ type: ATTEMPT_LISTENING, ...payload })
export const startListeningToCall = (payload) => ({ type: START_LISTENING, ...payload })
export const setAgentsAudioIssues = (payload) => ({ type: SET_AGENTS_AUDIO_ISSUES, payload })
export const patchAgentCallData = (agentUsername, callData) => ({
  type: PATCH_AGENT_CALL_DATA,
  payload: { agentUsername, callData },
})
export const setRosterInUse = (payload) => ({ type: SET_ROSTER_IN_USE, payload })
export const setFiltersLoaded = (payload) => ({ type: SET_FILTERS_LOADED, payload })
export const setSavedFilterId = (payload) => ({ type: SET_SAVED_FILTER_ID, payload })

// State
export const initialState = {
  agents: [],
  sentiment: {},
  sentimentConfig: {},
  filtersLoaded: false,
  rosterInUse: false,
  selectedSavedFilterId: null,
  socketOpen: false,
  loading: {
    agents: false,
    sentiment: false,
    sentimentUpsetReasons: false,
    sentimentPositiveReasons: false,
    sentimentConfig: false,
  },
  listeningToAgent: '',
  agentsAudioIssues: [],
}

// Reducer
const commandCenterReducer = (state = initialState, action) => {
  return produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case SET_AGENTS:
        newState.agents = commandCenterSort(payload)
        break
      case SET_AGENT:
        newState.agents = commandCenterSort(
          state.agents.map((agent) => {
            if (agent.agent_id === payload.agentId) {
              return payload.agentData
            }

            return agent
          })
        )
        break
      case SET_SENTIMENT_DATA:
        newState.sentiment = payload
        break
      case SET_SENTIMENT_CONFIG:
        newState.sentimentConfig = payload
        break
      case SET_LOADING:
        newState.loading = { ...newState.loading, ...payload }
        break
      case START_LISTENING:
        newState.listeningToAgent = action.agentUsername
        break
      case STOP_LISTENING:
        newState.listeningToAgent = ''
        break
      case SET_AGENTS_AUDIO_ISSUES:
        newState.agentsAudioIssues = payload
        break
      case SET_FILTERS_LOADED:
        newState.filtersLoaded = payload
        break
      case SET_ROSTER_IN_USE:
        newState.rosterInUse = payload
        break
      case PATCH_AGENT_CALL_DATA:
        // Merge, don't replace: the DB-backed fields (playbook, campaign) are
        // only refreshed when a row is expanded and must survive a boundary.
        newState.agents = commandCenterSort(
          state.agents.map((agent) =>
            agent.agent_username === payload.agentUsername
              ? { ...agent, call_data: { ...agent.call_data, ...payload.callData } }
              : agent
          )
        )
        break
      case SET_SAVED_FILTER_ID:
        newState.selectedSavedFilterId = payload
        break
      case SOCKET_OPEN:
        newState.socketOpen = true
        break
      case SOCKET_CLOSED:
        newState.socketOpen = false
        break
      default:
        break
    }
  })
}

export default commandCenterReducer
